import React from 'react';
import styled, {keyframes} from 'styled-components';
import {Accordion} from '../elements/Accordion';
import {Icon} from '../elements/Icon';
import {Switch} from '../elements/Switch';
import {TextInput} from '../elements/TextInput';
import {getColor} from '../utils/styled';
import {CONCRETE, GRAY150, GRAY_40, LOCHMARA, MINE_SHAFT} from '../refs/Colors';
import {ICON_HELP, ICON_PENCIL_MAGIC, ICON_PLUS, ICON_X, ICON_DELETE} from '../refs/Icons';
import {Toggleable} from "../types/Toggleable.ts";
import {InputPath} from "../types/InputPath.ts";
import {Tooltip} from "../index.tsx";
import {Anchor} from "../index.tsx";

type AiPanelProps = {
  className?: string;
  children?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
};

type AiPanelHeaderProps = {
  onClose?: () => void;
};

type AiPanelSectionsProps = {
  className?: string;
  toggleable: Toggleable;
  sections: string[];
  onAddSection: () => void;
  onRemoveSection?: (index: number) => void;
  newSection: InputPath<string>;
  hint?: string;
};

type AiPanelInfoProps = {
  toggleable: Toggleable,
  path: InputPath<string>,
  withStudyNotes: Toggleable,
  hasStudyNotes: boolean,
  merge: Toggleable,
  hasMerge: boolean,
  className?: string
};

type AiPanelInstructionsProps = {
  toggleable: Toggleable;
  path: InputPath<string>;
};

const slideRight = keyframes`
  from {
    transform: translate(-100%, 0);
  }
  to {
    transform: translate(0, 0);
  }
`;

const slideLeft = keyframes`
  from {
    transform: translate(0, 0);
  }
  to {
    transform: translate(-100%, 0);
  }
`;

const Panel = styled.div`
  position: absolute;
  top: 54px;
  left: 0;
  width: 100%;
  max-width: 380px;
  height: calc(100vh - 60px);
  background-color: ${getColor(CONCRETE)};
  border: 1px solid ${getColor(GRAY_40)};
  border-radius: 5px;
  box-shadow: 2px 2px 16px rgba(0, 0, 0, 0.08);
  padding: 30px 8px 32px 32px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  transform: translate(0, 0);

  &.is-left-side-panel {
    border-radius: 0 5px 5px 0;
    transform: translate(-100%, 0);
    animation: ${slideLeft} 0.3s forwards;
  }

  .with-left-side-panel &.is-left-side-panel {
      animation: ${slideRight} 0.4s forwards;
      transform: translate(0, 0);
  }

  &::before,
  &::after
  {
    content: "";
    display: block;
    width: calc(100% - 32px);
    height: 24px;
    position: absolute;
    left: 0;
  }

  &::before {
    top: 54px;
    background: linear-gradient(to bottom, ${getColor(CONCRETE)} 0%, rgba(242, 242, 242, 0) 100%);
  }

  &::after {
    bottom: 68px;
    background: linear-gradient(to top, ${getColor(CONCRETE)} 0%, rgba(242, 242, 242, 0) 100%);
  }

  .ai-section-hint {
    margin: 10px 0 18px;
  }

  .new-section-form {
    grid-column-start: 1;
    grid-column-end: 3;
  }

  .accordion {
    & .is-open,
    & .is-closed,
    & .accordion-header
    {
      padding-left: 2px;
      padding-right: 2px;
    }

    & .is-open {
      padding-top: 2px;
      padding-bottom: 2px;
    }
  }

  .ai-info-accordion,
  .ai-info-accordion .is-open,
  .ai-info-accordion .is-closed
  {
    height: 100%;
  }

  .ai-info-accordion .is-open,
  .ai-info-accordion .is-closed
  {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .ai-info-input {
    margin: 8px 0 0;
    height: 100%;
    min-height: 144px;
  }

  .ai-instructions-accordion .ai-instructions-input {
    margin-top: 8px;
    height: 114px;
  }

  @media (min-width: 1300px) {

    &.is-left-side-panel {
      border-radius: 5px;
    }

    left: auto;
    right: calc(50% + 260px);
  
  }

  .with-right-side-panel &.is-left-side-panel {
    
    @media (min-width: 1401px) {
      left: 0;
      right: auto;
    }

    @media (min-width: 1615px) {
      left: auto;
      right: calc(50% + 428px);
    }
      
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 24px;
`;

const Title = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
`;

const TitleText = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1;
  color: ${getColor(MINE_SHAFT)};
  margin: 0;
`;


const Body = styled.div`
  display: grid;
  grid-template-rows: auto 1fr auto;
  row-gap: 24px;
  padding: 24px 24px 24px 0;
  height: 100%;
  overflow: auto;
`;

const Description = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3em;
  color: ${getColor(GRAY150)};
  margin: 0;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 19px;
`;

const SectionHint = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1.3em;
  color: ${getColor(GRAY150)};
  margin: 0;
`;

const TagsWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
`;

const PillTag = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 6px 6px 10px;
  background-color: ${getColor(GRAY_40)};
  border-radius: 5px;
  width: 100%;
`;

const TagText = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 1em;
  color: ${getColor(MINE_SHAFT)};
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 17px;
`;

const SwitchRow = styled.div`
  display: block;
  margin-top: 16px;

  &.switch-row-els {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    position: relative;
  }

  .switch-help-icon {
    margin-left: 8px;
  }
`;


type AiPanelComponent = React.FC<AiPanelProps> & {
  Header: typeof AiPanelHeader;
  Sections: typeof AiPanelSections;
  Info: typeof AiPanelInfo;
  Instructions: typeof AiPanelInstructions;
};

export const AiPanel: AiPanelComponent = ({className, header, footer, children }) => (
  <Anchor.PortalContainer>
    <Panel className={className}>
      {header}
      <Body>
        <Description>
          Type or dictate the information and receive a formatted report draft in just a few seconds. PurviewAI will fill the report sections in the current template.
        </Description>
        {children}
      </Body>
      {footer}
    </Panel>
  </Anchor.PortalContainer>
);

export const AiPanelHeader: React.FC<AiPanelHeaderProps> = ({
  onClose,
}) => (
  <Header>
    <Title>
      <Icon icon={ICON_PENCIL_MAGIC} size={24} />
      <TitleText>Compose with AI</TitleText>
    </Title>
    <Icon data-testid='ai-close' icon={ICON_X} size={24} onClick={onClose} />
  </Header>
);

export const AiPanelSections: React.FC<AiPanelSectionsProps> = ({
  className,
  toggleable,
  sections = [],
  newSection,
  onAddSection,
  onRemoveSection,
  hint = "These sections will be filled with the content generated by PurviewAI. You can add new ones or remove them."
}) => (
  <SectionBlock className={className}>
    <Accordion data-testid='ai-sections' className="accordion" toggleable={toggleable} label='Report sections'>
      <SectionHint className="ai-section-hint">{hint}</SectionHint>
      <TagsWrap data-testid='ai-sections-wrap'>
        {sections.map((section, index) => (
          <PillTag key={`${section}-${index}`}>
            <TagText>{section}</TagText>
            {onRemoveSection && <Icon
              data-testid='ai-remove-section'
              icon={ICON_DELETE}
              size={16}
              color={LOCHMARA}
              onClick={() => onRemoveSection?.(index)}
            />}
          </PillTag>
        ))}
        <form className="new-section-form" onSubmit={(e) => {
          e.preventDefault();
          onAddSection();
        }}>
          <TextInput
            data-testid='ai-new-section'
            size={30}
            placeholder='Type a new section here'
            rightIcon={ICON_PLUS}
            onRightClick={onAddSection}
            path={newSection}
          />
        </form>
      </TagsWrap>
    </Accordion>
  </SectionBlock>
);

export const AiPanelInfo: React.FC<AiPanelInfoProps> = ({
  toggleable,
  path,
  withStudyNotes,
  hasStudyNotes,
  merge,
  hasMerge,
  className
}) => (
  <InfoBlock className={className}>
    <Accordion data-testid='ai-info' className="accordion ai-info-accordion" toggleable={toggleable} label='Type or dictate the information'>
      <TextInput
        data-testid='ai-info-input'
        className="ai-info-input"
        size={30}
        multiline={true}
        autoResize={false}
        path={path}
        placeholder='Type or dictate here. PurviewAI will create a report draft for you.'
        withEraser
      />
      {
        hasStudyNotes &&
        <SwitchRow onClick={withStudyNotes.toggle}>
          <Switch toggleable={{isOpen:withStudyNotes.isOpen}}>Use the study notes as context.</Switch>
        </SwitchRow>
      }
      {
        hasMerge &&
        <SwitchRow className="switch-row switch-row-els" onClick={merge.toggle}>
          <Switch toggleable={{isOpen: merge.isOpen}}>Use report content as context</Switch>
          <Tooltip.Trigger
            tooltip={<Tooltip arrowGap={4} my="bottom center" at="top right">PurviewAI will use your input and the current report content to generate a draft</Tooltip>}>
            <Icon className="switch-help-icon" icon={ICON_HELP} size={16} color={LOCHMARA} />
          </Tooltip.Trigger>
        </SwitchRow>
      }
    </Accordion>
  </InfoBlock>
);

export const AiPanelInstructions: React.FC<AiPanelInstructionsProps> = ({
  toggleable,
  path
}) => (
  <Accordion data-testid='ai-instructions' className="accordion ai-instructions-accordion" toggleable={toggleable} label='Additional formatting instructions'>
    <TextInput
      data-testid='ai-instructions-input'
      className="ai-instructions-input"
      multiline={true}
      autoResize={false}
      size={30}
      path={path}
      placeholder='Type or dictate here.'
      withEraser
    />
  </Accordion>
);

AiPanel.Header = AiPanelHeader;
AiPanel.Sections = AiPanelSections;
AiPanel.Info = AiPanelInfo;
AiPanel.Instructions = AiPanelInstructions;

export default AiPanel;
